import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, Subject } from 'rxjs';
import { share, takeUntil } from 'rxjs/operators';
import { BaseAPIObject } from '../shared/base-api.service';
import { ProgressBarService } from '../shared/progress-bar.service';

export interface LoginResponse {
  data?: any;
  statusCode: number;
  message: string;
  errors?: any;
}

export interface LoginPayload {
  email: string;
  password: string;
  remember_me: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private token = 'tsp-token';
  private baseUrl = '/api/auth'
  private unsubs = new Subject();

  constructor(
    private http: HttpClient,
    private router: Router,
    private jwtHelper: JwtHelperService,
    private loading: ProgressBarService
  ) { }

  auth({email, password, remember_me}: LoginPayload): Observable<LoginResponse> {
    const auth = 'Basic ' + btoa(`${email}:${password}`);
    const headers = new HttpHeaders().append('Authorization', auth);
    const url = this.baseUrl + '/login';
    return this.http.post<LoginResponse>(url, { remember_me }, { headers });
  }

  logout(): void {
    localStorage.clear();
    this.router.navigate(['/login']);
  }

  registerOrganization(data: any): Promise<any>{
    return new Promise((resolve) => {
      this.loading.show();
      const url = this.baseUrl + '/register-organization'
      this.http.post<BaseAPIObject<any>>(url, data).pipe(share()).subscribe({
        next: ({data}) => {
          return resolve(data);
        },
        error: err => {
          this.loading.hide();
          return Promise.reject(err)
        }
      })
    })
  }

  getToken(): string {
    return localStorage.getItem(this.token);
  }

  removeToken(): void {
    localStorage.removeItem(this.token);
  }

  getLoggedUser(): any {
    const token = localStorage.getItem(this.token);
    const decodedToken = this.jwtHelper.decodeToken<any>(token);
    return decodedToken ? decodedToken.data.user : null;
  }

  setToken(token: string): void {
    localStorage.setItem(this.token, token);
  }

  forgotPassword(email: string): Observable<any>{
    const url = this.baseUrl + "/forgot-password";
    const body = { credential: email };
    return this.http.post<any>(url, body);
  }

  restorePassword(data: any): Observable<any>{
    const url = this.baseUrl + "/restore-password";
    return this.http.post<any>(url, data);
  }

  isAuthenticated(): boolean {
    return !this.jwtHelper.isTokenExpired(this.getToken());
  }

  isSuperadmin(): boolean {
    return this.getLoggedUser().role.name == 'superadmin'
  }
}
